'use client'

import { Disclaimer, PlainButton, Span } from '@pcln/horizon'
import {
  AiSearchFilterChips,
  AiSearchInput,
  type LocationSearchType,
  type RenderAutocomplete,
} from '@pcln/horizon-search-form'

import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'

import {
  AI_SEARCH_ANIMATED_PLACEHOLDERS,
  AI_SEARCH_HELPER_TEXT,
  AI_SEARCH_INPUT_LABEL,
  AI_SEARCH_MIN_QUERY_LENGTH,
  AI_SEARCH_NO_RECOMMENDATIONS_AFTER_LINK,
  AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL,
  AI_SEARCH_PLACEHOLDER,
  AI_SEARCH_RESOLVE_ERROR_BEFORE_LINK,
} from './aiSearchConstants'
import { AiSearchSuggestions } from './AiSearchSuggestions'
import { useAiSearchForm, type AiSearchSubmitParams } from './useAiSearchForm'

export type { AiSearchSubmitParams }

type AiSearchFormProps = {
  isMobile?: boolean
  initialStartDate?: string
  initialEndDate?: string
  initialTravelers?: TravelerState
  onSubmitQuery: (params: AiSearchSubmitParams) => Promise<void> | void
  /** Called with the query that returned no match when the user opts into Standard Search. */
  onSwitchToStandardSearch?: (searchQuery: string) => void
  renderCityAutocomplete?: RenderAutocomplete
}

export function AiSearchForm({
  isMobile = false,
  initialStartDate,
  initialEndDate,
  initialTravelers,
  onSubmitQuery,
  onSwitchToStandardSearch,
  renderCityAutocomplete,
}: Readonly<AiSearchFormProps>) {
  const {
    inputValue,
    interpretation,
    isSubmitting,
    isFetching,
    showFilterChips,
    showStandardSearchFallback,
    showExamples,
    isSubmitDisabled,
    inputId,
    helperTextId,
    submitErrorId,
    inputDescribedBy,
    removableFilters,
    handleFormSubmit,
    handleInputSubmit,
    handleInputChange,
    handleDatesChange,
    handleTravelersChange,
    handleCityChange,
    handleRemoveFilter,
    handleExampleSelect,
  } = useAiSearchForm({
    initialStartDate,
    initialEndDate,
    initialTravelers,
    onSubmitQuery,
  })

  return (
    <div className='flex w-full flex-col gap-3'>
      <form
        aria-busy={isSubmitting}
        aria-label='AI search form'
        className='flex w-full flex-col gap-3'
        noValidate
        onSubmit={handleFormSubmit}
      >
        <AiSearchInput
          aria-describedby={inputDescribedBy}
          aria-label={AI_SEARCH_INPUT_LABEL}
          animatedPlaceholders={[...AI_SEARCH_ANIMATED_PLACEHOLDERS]}
          className='w-full'
          disabled={isSubmitting}
          id={inputId}
          minSubmitLength={AI_SEARCH_MIN_QUERY_LENGTH}
          onChange={handleInputChange}
          onSubmit={handleInputSubmit}
          placeholder={AI_SEARCH_PLACEHOLDER}
          submitDisabled={isSubmitDisabled}
          value={inputValue}
        />
      </form>

      {showFilterChips ? (
        <AiSearchFilterChips
          city={(interpretation.city as LocationSearchType | null) ?? undefined}
          endDate={interpretation.endDate}
          onCityChange={(city) => handleCityChange(city as LOCATION_SEARCH_TYPE | null)}
          onDatesChange={handleDatesChange}
          onRemoveFilter={handleRemoveFilter}
          onTravelersChange={handleTravelersChange}
          removableFilters={removableFilters}
          renderCityAutocomplete={renderCityAutocomplete}
          startDate={interpretation.startDate}
          travelers={interpretation.travelers}
          isLoading={isFetching || isSubmitting}
        />
      ) : null}

      <Span id={helperTextId} palette='neutral' shade='10' textStyle='body2'>
        {AI_SEARCH_HELPER_TEXT}
      </Span>

      {showStandardSearchFallback ? (
        <div data-testid='ai-search-submit-error' id={submitErrorId} role='alert'>
          <Disclaimer className='px-0 py-0'>
            {AI_SEARCH_RESOLVE_ERROR_BEFORE_LINK}
            {onSwitchToStandardSearch ? (
              <PlainButton
                onClick={() => onSwitchToStandardSearch(inputValue.trim())}
                type='primary'
                variant='inline'
              >
                {AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL}
              </PlainButton>
            ) : (
              AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL
            )}
            {AI_SEARCH_NO_RECOMMENDATIONS_AFTER_LINK}
          </Disclaimer>
        </div>
      ) : null}

      {showExamples ? <AiSearchSuggestions isMobile={isMobile} onSelect={handleExampleSelect} /> : null}
    </div>
  )
}
